import React, { useEffect, useState, useCallback } from 'react';
import { BellRing, Wallet, Users, Sparkles, Loader2, MessageCircle, Copy, Clock } from 'lucide-react';
import api from '@/lib/api';
import { formatRp, waLink } from '@/lib/format';
import { LeadBadge } from '@/components/StatusBadge';
import { ListSkeleton } from '@/components/Skeletons';
import { EmptyState } from '@/components/EmptyState';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

export default function FollowUp() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [draftOpen, setDraftOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const [draftLoading, setDraftLoading] = useState(false);
  const [target, setTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/followups'); setData(r.data); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const makeDraft = async (tipe, nama, detail, whatsapp) => {
    setTarget({ nama, whatsapp });
    setDraft(''); setDraftOpen(true); setDraftLoading(true);
    try {
      const r = await api.post('/ai/followup-draft', { tipe, nama, detail });
      setDraft(r.data.draft);
    } catch (e) { !e?.__gated && toast.error(e?.response?.data?.detail || 'Gagal membuat draft'); setDraft(''); } finally { setDraftLoading(false); }
  };

  const copyDraft = () => { navigator.clipboard?.writeText(draft); toast.success('Pesan disalin'); };
  const sendWA = () => { if (target?.whatsapp) window.open(waLink(target.whatsapp, draft), '_blank'); else { copyDraft(); toast.info('Nomor WA kosong, pesan disalin.'); } };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="flex items-center gap-2 font-display text-xl font-bold"><BellRing className="h-5 w-5 text-primary" /> Follow-up</h1>
        <p className="text-sm text-muted-foreground">Tagih piutang & hidupkan lead yang mandek — dibantu draft AI.</p>
      </div>

      {loading || !data ? <ListSkeleton /> : (
        <Tabs defaultValue="piutang">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="piutang" data-testid="followup-tab-piutang">Piutang ({data.overdue_payments.length})</TabsTrigger>
            <TabsTrigger value="lead" data-testid="followup-tab-lead">Lead Mandek ({data.stale_leads.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="piutang" className="mt-3 space-y-2.5">
            {data.overdue_payments.length === 0 ? <EmptyState icon={Wallet} title="Tidak ada piutang" description="Semua proyek sudah lunas. Mantap!" /> : data.overdue_payments.map((o) => (
              <div key={o.project_id} className="rounded-xl border bg-card p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2"><span className="truncate font-semibold">{o.customer_name}</span>{o.jatuh_tempo && <Badge variant="outline" className="rounded-full border-red-200 bg-red-50 text-red-700">Jatuh tempo</Badge>}</div>
                    <div className="truncate text-xs text-muted-foreground">{o.nama_proyek}</div>
                    <div className="mt-1 flex items-center gap-1 text-xs text-muted-foreground"><Clock className="h-3 w-3" /> {o.hari_sejak_bayar} hari sejak bayar terakhir</div>
                  </div>
                  <div className="text-right"><div className="font-display font-semibold text-orange-600 tabular-nums">{formatRp(o.sisa)}</div></div>
                </div>
                <div className="mt-2 flex gap-2">
                  <Button size="sm" onClick={() => makeDraft('piutang', o.customer_name, { proyek: o.nama_proyek, sisa: o.sisa, nilai: o.nilai, sudah_dibayar: o.sudah_dibayar }, o.whatsapp)} className="rounded-lg" data-testid="followup-draft-button"><Sparkles className="mr-1.5 h-4 w-4" /> Draft AI</Button>
                  {o.whatsapp && <a href={waLink(o.whatsapp)} target="_blank" rel="noreferrer" className="flex h-9 items-center gap-1.5 rounded-lg border px-3 text-sm hover:bg-muted"><MessageCircle className="h-4 w-4 text-accent" /> WA</a>}
                </div>
              </div>
            ))}
          </TabsContent>

          <TabsContent value="lead" className="mt-3 space-y-2.5">
            {data.stale_leads.length === 0 ? <EmptyState icon={Users} title="Tidak ada lead mandek" description="Semua lead sudah ditindaklanjuti." /> : data.stale_leads.map((l) => (
              <div key={l.customer_id} className="rounded-xl border bg-card p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2"><span className="truncate font-semibold">{l.nama}</span><LeadBadge status={l.status} /></div>
                    <div className="mt-1 flex items-center gap-1 text-xs text-muted-foreground"><Clock className="h-3 w-3" /> {l.hari_sejak_kontak} hari tanpa kontak</div>
                    {l.catatan && <div className="truncate text-xs text-muted-foreground">{l.catatan}</div>}
                  </div>
                </div>
                <div className="mt-2 flex gap-2">
                  <Button size="sm" onClick={() => makeDraft('lead', l.nama, { status: l.status, catatan: l.catatan, hari_sejak_kontak: l.hari_sejak_kontak }, l.whatsapp)} className="rounded-lg" data-testid="followup-draft-button"><Sparkles className="mr-1.5 h-4 w-4" /> Draft AI</Button>
                  {l.whatsapp && <a href={waLink(l.whatsapp)} target="_blank" rel="noreferrer" className="flex h-9 items-center gap-1.5 rounded-lg border px-3 text-sm hover:bg-muted"><MessageCircle className="h-4 w-4 text-accent" /> WA</a>}
                </div>
              </div>
            ))}
          </TabsContent>
        </Tabs>
      )}

      <Dialog open={draftOpen} onOpenChange={setDraftOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display">Draft Pesan untuk {target?.nama}</DialogTitle></DialogHeader>
          {draftLoading ? (
            <div className="flex items-center justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-primary" /><span className="ml-2 text-sm text-muted-foreground">AI sedang menulis...</span></div>
          ) : (
            <Textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={8} className="text-sm" data-testid="followup-draft-text" />
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={copyDraft} className="rounded-xl"><Copy className="mr-2 h-4 w-4" /> Salin</Button>
            <Button onClick={sendWA} className="rounded-xl bg-accent text-accent-foreground hover:bg-accent/90"><MessageCircle className="mr-2 h-4 w-4" /> Kirim WhatsApp</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
